var _sentryCollisionFreeGlobalObject = typeof window != "undefined" ? window : typeof global != "undefined" ? global : typeof self != "undefined" ? self : {};
_sentryCollisionFreeGlobalObject["__sentryRewritesTunnelPath__"] = undefined;
_sentryCollisionFreeGlobalObject["SENTRY_RELEASE"] = {"id":"joyboy-web@uk-prod@1.64.0"};
_sentryCollisionFreeGlobalObject["__sentryBasePath"] = undefined;
_sentryCollisionFreeGlobalObject["__rewriteFramesAssetPrefixPath__"] = "/assets/web";

// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from '@sentry/nextjs';
import { EcEnv } from '@castlery/config';
import { logger } from '@castlery/observability';

function initSentry(): void {
  // Skip initialization in development environment
  if (EcEnv.NODE_ENV === 'development') {
    return;
  }

  // Validate required environment variables
  if (!process?.env?.NEXT_PUBLIC_SENTRY_DSN || !process?.env?.NEXT_PUBLIC_APPLICATION_ENV) {
    logger.warn(
      'Sentry initialization failed: Missing required environment variables (NEXT_PUBLIC_SENTRY_DSN or NEXT_PUBLIC_APPLICATION_ENV)'
    );
    return;
  }

  const appVersion = process.env.NEXT_PUBLIC_VERSION || '0.0.0';
  const appEnv = process.env.NEXT_PUBLIC_APPLICATION_ENV;

  try {
    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      environment: appEnv,
      release: `joyboy-web@${appEnv}@${appVersion}`,

      // Performance monitoring
      tracesSampleRate: 0.001,

      // Session replay settings
      replaysOnErrorSampleRate: 0.001,
      replaysSessionSampleRate: 0.001,

      // Default tags
      initialScope: {
        tags: {
          app: 'web-client',
          version: appVersion,
        },
      },

      // Disable in development, enable in debug mode when troubleshooting issues
      // Set to true to see which errors are being filtered in console
      debug: false,

      // Only keep the most specific ignoreErrors patterns that are definitely noise
      // ⚠️ IMPORTANT: Do NOT add critical services here (DynamicYield, Algolia, Stripe, etc.)
      ignoreErrors: [
        // Common browser issues that don't affect user experience
        'Non-Error promise rejection captured',
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Specific third-party script errors that are known noise
        'Javascript error: Script error. on line 0',

        // Browser extension errors
        /chrome-extension:\/\//i,
        /moz-extension:\/\//i,
        /safari-extension:\/\//i,

        // Common iOS Safari errors
        'WebKitBlobResource error',
        "Can't find variable: webkit",

        // Hydration warnings (handled by React, not user-facing errors)
        /Hydration failed/i,
        /There was an error while hydrating/i,
        /Text content does not match server-rendered HTML/i,

        // Aborted requests (user navigated away)
        'AbortError: The operation was aborted',
        'The user aborted a request',

        // Synchronous XHR errors during page dismissal (harmless browser behavior)
        /Synchronous XHR in page dismissal/i,
        /Failed to load.*Synchronous XHR in page dismissal/i,

        // Storage access errors (privacy mode, browser settings, etc.)
        /Failed to read the 'sessionStorage' property from 'Window': Access is denied/i,
        /Failed to read the 'localStorage' property from 'Window': Access is denied/i,
        /SecurityError.*sessionStorage/i,
        /SecurityError.*localStorage/i,

        // Random script errors with no stack trace
        'Script error',

        // Chunk loading errors (usually retry mechanisms handle these)
        /Loading chunk \d+ failed/i,
        /ChunkLoadError/i,

        // Third-party service errors (NON-CRITICAL analytics only)
        /google-analytics/i,
        /googletagmanager/i,
        /gtag/i,

        // ⚠️ NOTE: Stripe/PayPal errors are NOT filtered - they're critical services
        // ⚠️ NOTE: DynamicYield errors are NOT filtered - can cause white screen
        // ⚠️ NOTE: Algolia errors are NOT filtered - affects search functionality

        // Content Security Policy violations from third parties
        /Refused to execute inline script/i,

        // Safari specific issues
        /webkit-masked-url/i,
        /evaluating 'webkit'/i,

        // Ad blockers and privacy extensions
        /adblock/i,
        /privacy-badger/i,
        /ghostery/i,

        // Third-party analytics and marketing tools (based on production data)
        /rudderanalytics/i,
        /klaviyo/i,
        /cookieyes/i,
        /friendbuy/i,
        /mulberry/i,
        /getmulberry/i,
        /ptengine/i,

        // Third-party ad services
        /adsbygoogle/i,
        /teads/i,

        // Browser-specific errors
        /evaluating 'window\.webkit'/i,
        /window\.webkit\.messageHandlers/i,
        /messageHandlers/i,
        /_AutofillCallbackHandler/i,
      ],

      // Custom error filtering with context-aware logic
      beforeSend(event, hint) {
        const error = hint.originalException;
        const errorMessage = event.message || '';
        const userAgent = navigator?.userAgent || '';
        const exception = event.exception?.values?.[0];
        const stacktrace = exception?.stacktrace;
        const frames = stacktrace?.frames || []; // Define frames early for use throughout the function

        // 0. Filter Klaviyo-related stack overflow errors (high priority)
        if (errorMessage.includes('Maximum call stack') || errorMessage.includes('call stack size exceeded')) {
          const isKlaviyoError = frames.some((frame) => {
            const filename = frame.filename || '';
            const functionName = frame.function || '';
            return (
              filename.includes('klaviyo') ||
              functionName.includes('_learnq') ||
              functionName.includes('window._learnq')
            );
          });

          if (isKlaviyoError || errorMessage.includes('_learnq') || errorMessage.includes('klaviyo')) {
            logger.debug('Sentry: Ignoring Klaviyo stack overflow error:', { errorMessage });
            return null;
          }
        }

        // 1. Filter errors without stack traces (usually noise)
        if (exception && !stacktrace?.frames?.length) {
          logger.debug('Sentry: Ignoring error without stack trace:', { errorMessage });
          return null;
        }

        // 2. Filter WebView bridge errors during page unload (Facebook in-app browser, etc.)
        // These occur when JavaScript tries to call native Java methods during page unload,
        // but the Java object has already been destroyed. This is harmless and not actionable.
        const extraData = (event.extra as any)?.arguments?.[0];
        const isBeforeUnloadEvent = extraData?.type === 'beforeunload' || extraData?.type === 'unload';

        if (isBeforeUnloadEvent) {
          // Filter Java bridge errors in WebView (Facebook app, etc.)
          if (
            errorMessage.includes('Java object is gone') ||
            errorMessage.includes('enableDidUserTypeOnKeyboardLogging') ||
            errorMessage.includes('Error invoking')
          ) {
            console.debug('Sentry: Ignoring WebView Java bridge error during page unload:', errorMessage);
            return null;
          }

          // Filter synchronous XHR errors during page dismissal
          // Browsers block synchronous XHR requests during page unload for security/performance reasons
          if (
            errorMessage.includes('Synchronous XHR in page dismissal') ||
            errorMessage.includes("Failed to execute 'send' on 'XMLHttpRequest'")
          ) {
            logger.debug('Sentry: Ignoring synchronous XHR error during page dismissal:', { errorMessage });
            return null;
          }
        }

        // 3. Network errors: only ignore if user is offline OR from third-party scripts
        const networkErrors = ['Load failed', 'Network request failed', 'Failed to fetch', 'NetworkError'];
        if (networkErrors.some((pattern) => errorMessage.includes(pattern))) {
          if (navigator?.onLine === false) {
            logger.debug('Sentry: Ignoring network error (user offline):', { errorMessage });
            return null;
          }

          // Filter network errors from Mulberry plugin (warranty provider) during page unload
          // These are harmless - browser cancels requests during unload
          // This includes "Synchronous XHR in page dismissal" errors which occur when
          // third-party scripts try to send requests during page dismissal (beforeunload/unload)
          const isMulberryError = frames.some((frame) => {
            const filename = frame.filename || '';
            return filename.includes('mulberry') || filename.includes('getmulberry');
          });

          if (isMulberryError) {
            // Check if error occurred during page unload (beforeunload or unload events)
            const isUnloadEvent =
              extraData?.type === 'unload' ||
              extraData?.type === 'beforeunload' ||
              errorMessage.includes('unload') ||
              errorMessage.includes('page dismissal') ||
              errorMessage.includes('Synchronous XHR in page dismissal');

            if (isUnloadEvent) {
              logger.debug('Sentry: Ignoring Mulberry network error during page unload:', { errorMessage });
              return null;
            }
            // If not during unload, keep the error as it might be a real issue
          }

          // Filter network errors from non-critical third-party marketing/analytics scripts
          const nonCriticalThirdPartyScripts = [
            'cookieyes',
            'ptengine',
            'klaviyo',
            'rudderstack',
            'friendbuy',
            'google-analytics',
            'googletagmanager',
            'gtag',
          ];

          const isNonCriticalThirdPartyScript = frames.some((frame) => {
            const filename = frame.filename || '';
            return nonCriticalThirdPartyScripts.some((script) => filename.toLowerCase().includes(script));
          });

          if (isNonCriticalThirdPartyScript) {
            logger.debug('Sentry: Ignoring non-critical third-party script network error:', { errorMessage });
            return null;
          }

          // Allow network errors when user is online as they might indicate real issues
        }

        // 4. Generic script errors: only ignore from known third-party domains
        if (errorMessage === 'Script error.' && exception) {
          const filename = stacktrace?.frames?.[0]?.filename;
          if (
            filename &&
            (filename.includes('clarity.ms') ||
              filename.includes('creativecdn.com') ||
              filename.includes('pinterest.com') ||
              filename.includes('facebook.com') ||
              filename.includes('connect.facebook.net') ||
              filename.includes('googletagmanager.com') ||
              filename.includes('google-analytics.com') ||
              filename.includes('cookieyes') ||
              filename.includes('klaviyo') ||
              filename.includes('ptengine'))
          ) {
            logger.debug('Sentry: Ignoring script error from third-party domain:', { filename });
            return null;
          }
        }

        // 5. Filter errors from third-party scripts in stack trace
        const hasOwnCode = frames.some((frame) => {
          const filename = frame.filename || '';
          // Exclude known third-party modules (ATS, API scripts, etc.)
          if (
            filename.includes('ats-modules') ||
            filename.includes('app:///ats') ||
            filename.includes('api_static') ||
            filename.includes('app:///api/')
          ) {
            return false;
          }
          // Consider it our code if it's from our domain or webpack chunks
          return (
            filename.includes('/_next/') ||
            filename.includes('/static/') ||
            (filename.includes('.js') && !filename.includes('http'))
          );
        });

        // Critical third-party services that affect user experience
        // These errors should NEVER be filtered even if they're from third-party code
        const criticalThirdPartyServices = [
          'dynamicyield', // DY personalization - can cause white screen
          'dy.com',
          'stripe.com/v3', // Payment processing
          'paypal.com/sdk', // Payment processing
          // Add more critical services here as needed
        ];

        const isCriticalThirdParty = frames.some((frame) => {
          const filename = frame.filename || '';
          return criticalThirdPartyServices.some((service) => filename.toLowerCase().includes(service));
        });

        // Only filter if:
        // 1. No our own code AND
        // 2. Not a critical third-party service
        if (!hasOwnCode && !isCriticalThirdParty && frames.length > 0) {
          const firstFrame = frames[0]?.filename || '';
          logger.debug('Sentry: Ignoring error from third-party code only:', { firstFrame });
          return null;
        }

        // If it's a critical third-party error, add a tag for visibility
        if (isCriticalThirdParty && !hasOwnCode) {
          if (!event.tags) event.tags = {};
          event.tags.criticalThirdParty = 'yes';
          logger.debug('Sentry: KEEPING critical third-party error:', { errorMessage });
        }

        // 6. Filter errors from specific mobile browsers/apps based on user agent
        const mobileAppPatterns = [
          /FBAN|FBAV/i, // Facebook app
          /Instagram/i, // Instagram app
          /Line\//i, // Line app
          /MicroMessenger/i, // WeChat
        ];

        if (mobileAppPatterns.some((pattern) => pattern.test(userAgent))) {
          // Only filter out known problematic error types in these browsers
          const mobileNoiseErrors = [/TypeError.*Load failed/i, /.*facebook.*/i, /.*instagram.*/i, /webkit/i];

          if (mobileNoiseErrors.some((pattern) => pattern.test(errorMessage))) {
            logger.debug('Sentry: Ignoring mobile app browser error:', { errorMessage, userAgent });
            return null;
          }
        }

        // 7. Filter cancelled/aborted requests
        if ((error as Error)?.name === 'AbortError' || errorMessage.includes('abort')) {
          logger.debug('Sentry: Ignoring aborted request:', { errorMessage });
          return null;
        }

        // 8. Filter timeout errors from third-party services
        if (errorMessage.includes('timeout') && !hasOwnCode) {
          logger.debug('Sentry: Ignoring third-party timeout:', { errorMessage });
          return null;
        }

        // 9. Filter "null is not an object" errors without our code
        if (errorMessage.includes('null is not an object') && !hasOwnCode) {
          logger.debug('Sentry: Ignoring third-party null error:', { errorMessage });
          return null;
        }

        // 10. Filter "undefined is not an object/function" errors without our code
        if (
          (errorMessage.includes('undefined is not an object') ||
            errorMessage.includes('undefined is not a function')) &&
          !hasOwnCode
        ) {
          logger.debug('Sentry: Ignoring third-party undefined error:', { errorMessage });
          return null;
        }

        // 11. Filter "Cannot read property" errors from third-party code
        if (errorMessage.includes('Cannot read propert') && !hasOwnCode) {
          logger.debug('Sentry: Ignoring third-party read property error:', { errorMessage });
          return null;
        }

        // 12. Filter cross-origin errors (CORS issues from third parties)
        if (errorMessage.toLowerCase().includes('cross-origin') && !hasOwnCode) {
          logger.debug('Sentry: Ignoring third-party CORS error:', { errorMessage });
          return null;
        }

        // 13. Filter specific third-party error patterns from production
        const thirdPartyPatterns = [
          /mutation\.target\.matches/i, // Third-party DOM manipulation
          /api_static\.js/i, // Third-party API scripts
          /frame_ant/i, // Frame ant third-party
          /inject_content/i, // Browser extension content injection
          /translate_http/i, // Google Translate
          /MetaMask/i, // MetaMask extension
          /extensionAPIBridge/i, // Browser extension
        ];

        if (thirdPartyPatterns.some((pattern) => pattern.test(errorMessage)) && !hasOwnCode) {
          logger.debug('Sentry: Ignoring known third-party error pattern:', { errorMessage });
          return null;
        }

        // 14. Filter ATS third-party module errors
        const isAtsError = frames.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('ats-modules') || filename.includes('app:///ats');
        });

        if (isAtsError) {
          logger.debug('Sentry: Ignoring ATS third-party module error:', { errorMessage });
          return null;
        }

        // 15. Filter third-party API scripts errors (api_static.js, etc.)
        const isApiScriptError = frames.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('api_static') || filename.includes('app:///api/');
        });

        if (isApiScriptError) {
          logger.debug('Sentry: Ignoring third-party API script error:', { errorMessage });
          return null;
        }

        // 16. Filter errors from HeadlessChrome (crawlers/bots)
        if (userAgent.includes('HeadlessChrome')) {
          logger.debug('Sentry: Ignoring error from HeadlessChrome (bot/crawler):', { errorMessage });
          return null;
        }

        // 17. Filter webkit.messageHandlers errors (iOS WebView bridge errors)
        // These occur when third-party code tries to access iOS WebView APIs that don't exist
        // in regular web browsers (like Instagram in-app browser)
        if (
          errorMessage.includes('messageHandlers') ||
          errorMessage.includes('window.webkit.messageHandlers') ||
          (errorMessage.includes('webkit') && errorMessage.includes('undefined is not an object'))
        ) {
          logger.debug('Sentry: Ignoring webkit.messageHandlers error:', { errorMessage });
          return null;
        }

        // 18. Filter errors from specific mobile apps based on browser name
        const mobileBrowsers = ['Instagram', 'Google', 'Facebook'];
        const browserName = event.tags?.['browser.name'] || event.request?.headers?.['User-Agent'] || '';

        if (mobileBrowsers.some((name) => String(browserName).includes(name))) {
          // Filter webkit errors in mobile apps
          if (errorMessage.includes('webkit')) {
            logger.debug('Sentry: Ignoring webkit error in mobile app browser:', { browserName });
            return null;
          }
          // Filter Maximum call stack in mobile apps
          if (errorMessage.includes('Maximum call stack')) {
            logger.debug('Sentry: Ignoring stack overflow in mobile app browser:', { browserName });
            return null;
          }
        }

        // 19. Add additional context to remaining errors
        if (event.tags) {
          event.tags.userAgent = userAgent;
          event.tags.onlineStatus = navigator?.onLine ? 'online' : 'offline';
          event.tags.hasOwnCode = hasOwnCode ? 'yes' : 'no';
        }

        // 20. Add breadcrumbs context
        if (!event.contexts) {
          event.contexts = {};
        }
        event.contexts.browser = {
          userAgent,
          online: navigator?.onLine,
          language: navigator?.language,
          name: event.tags?.['browser.name'] || '',
        };

        return event;
      },

      denyUrls: [
        // ⚠️ IMPORTANT: Only block NON-CRITICAL third-party services
        // DO NOT add: DynamicYield, Algolia, Stripe, PayPal - these are critical!

        // Analytics and tracking services (non-critical)
        /clarity\.ms/i,
        /creativecdn\.com/i,
        /pinterest\.com/i,
        /google-analytics\.com/i,
        /googletagmanager\.com/i,
        /analytics\.tiktok\.com/i,
        /bat\.bing\.com/i,
        /doubleclick\.net/i,

        // Social media
        /graph\.facebook\.com/i,
        /connect\.facebook\.net/i,
        /platform\.twitter\.com/i,
        /platform\.instagram\.com/i,
        /cdn\.syndication\.twimg\.com/i,

        // Woopra analytics
        /eatdifferent\.com\.woopra-ns\.com/i,
        /static\.woopra\.com/i,

        // Browser extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,

        // Third-party widgets and plugins
        /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
        /webappstoolbarba\.texthelp\.com/i,
        /metrics\.itunes\.apple\.com\.edgesuite\.net/i,

        // CDN and third-party scripts that may have CORS issues
        /cdn\.jsdelivr\.net/i,
        /unpkg\.com/i,
        /cdnjs\.cloudflare\.com/i,

        // Third-party marketing and analytics (from production data)
        /friendbuy/i,
        /cookieyes/i,
        /klaviyo/i,
        /rudderstack/i,
        /teads-fellow/i,
        /getmulberry\.com/i,
        /mulberry/i,
        /ptengine\.com/i,

        // Browser extensions and tools
        /metamask/i,
        /wallet-extension/i,
      ],
    });
  } catch (error) {
    logger.error('Failed to initialize Sentry on client', {
      error,
      environment: appEnv,
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN ? 'configured' : 'missing',
    });
  }
}

initSentry();
